import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  BuildingOffice2Icon,
  Cog6ToothIcon,
  HomeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { BrandLogo } from './Logo';

export const CompanyLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navigation = [
    { name: 'Workspaces',   href: '/company',          icon: BuildingOffice2Icon, current: location.pathname === '/company' },
    { name: 'Group Report', href: '/company/group-report', icon: ChartBarIcon,    current: location.pathname === '/company/group-report' },
    { name: 'Facebook App', href: '/company/facebook', icon: Cog6ToothIcon,       current: location.pathname === '/company/facebook' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const pageLabel = navigation.find(n => n.current)?.name || 'Company Console';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-[260px] w-full bg-gray-900 shadow-2xl">
            <div className="absolute top-3 -right-12">
              <button className="flex items-center justify-center h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20" onClick={() => setSidebarOpen(false)}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <CompanySidebar navigation={navigation} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-60">
          <CompanySidebar navigation={navigation} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <header className="relative z-10 flex-shrink-0 flex h-16 bg-white border-b border-gray-200 shadow-sm">
          <button className="lg:hidden px-4 text-gray-500 hover:text-gray-700" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1 flex items-center justify-between px-5">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <HomeIcon className="h-4 w-4" />
              <span>/</span>
              <span className="text-gray-800 font-medium">{pageLabel}</span>
            </div>

            <div className="relative">
              <button onClick={() => setProfileOpen(!profileOpen)} className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-semibold text-gray-800 leading-tight">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-gray-400 leading-tight">Company</p>
                </div>
                <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                    <span className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700">Company</span>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/company/profile"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <UserCircleIcon className="h-4 w-4 text-gray-400" />
                      Your Profile
                    </Link>
                    <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6 px-5 sm:px-8 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const CompanySidebar = ({ navigation }) => (
  <div className="flex flex-col h-full bg-gray-900">
    <div className="flex-shrink-0 px-3 py-2 border-b border-gray-800">
      <BrandLogo />
    </div>
    <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
      {navigation.map((item) => (
        <Link
          key={item.name}
          to={item.href}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            item.current ? 'bg-accent-500 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
        >
          <item.icon className="h-5 w-5 flex-shrink-0" />
          {item.name}
        </Link>
      ))}
    </nav>
    <div className="px-3 py-3 border-t border-gray-800">
      <p className="text-xs text-gray-600 text-center">LMS v1.0</p>
    </div>
  </div>
);
